import { c as _c } from "react/compiler-runtime";
import { IcoLock16, IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import type { AgentTool, ProxyConfigBasic, VaultProxyTool } from '@onefootprint/request-types/dashboard';
import { Button, Card, IconButton, Select, Shimmer, TextInput } from '@onefootprint/ui';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ErrorComponent from '@/components/error';
import useAllProxyConfigs from '@/hooks/use-all-proxy-configs';
import usePermissions from '@/hooks/use-permissions';
import useSession from '@/hooks/use-session';
import { useBuilder } from '../../store/provider';
import { isVaultProxyTool } from '../../store/types';
type Slot = 'liveConfigId' | 'sandboxConfigId';
type Option = {
  value: string;
  label: string;
};
const NOT_SET = 'not_set';
// Percy names the tool after it, so the backend allows only lowercase words joined by single underscores.
const TOOL_NAME = /^[a-z0-9]+(_[a-z0-9]+)*$/;
const withSlot = (data: VaultProxyTool, slot: Slot, configId: string | undefined): VaultProxyTool => slot === 'liveConfigId' ? {
  ...data,
  liveConfigId: configId
} : {
  ...data,
  sandboxConfigId: configId
};
type VaultProxyRowProps = {
  id: string;
  name: ReactNode;
  data: VaultProxyTool;
  sandboxOptions: Option[];
  liveOptions?: Option[];
  canChange: boolean;
  onChange: (slot: Slot, value: string) => void;
  onRemove: () => void;
  children?: ReactNode;
};
const VaultProxyRow = t0 => {
  const $ = _c(38);
  const {
    id,
    name,
    data,
    sandboxOptions,
    liveOptions,
    canChange,
    onChange,
    onRemove,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.tools.vaultProxy"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    isReadOnly,
    isSavingGraph
  } = useBuilder();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span aria-hidden={true} className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-tertiary bg-secondary text-secondary"><IcoLock16 /></span>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `vault-proxy-${id}-sandbox`;
  let t4;
  if ($[2] !== t) {
    t4 = t(_temp);
    $[2] = t;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = data.sandboxConfigId ?? NOT_SET;
  const t6 = !canChange || isSavingGraph;
  let t7;
  if ($[4] !== onChange) {
    t7 = value => onChange("sandboxConfigId", value);
    $[4] = onChange;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== sandboxOptions || $[7] !== t3 || $[8] !== t4 || $[9] !== t5 || $[10] !== t6 || $[11] !== t7) {
    t8 = <Select id={t3} label={t4} size="compact" value={t5} options={sandboxOptions} disabled={t6} onChange={t7} />;
    $[6] = sandboxOptions;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== canChange || $[14] !== data.liveConfigId || $[15] !== id || $[16] !== isSavingGraph || $[17] !== liveOptions || $[18] !== onChange || $[19] !== t) {
    t9 = liveOptions && <Select id={`vault-proxy-${id}-live`} label={t(_temp2)} size="compact" value={data.liveConfigId ?? NOT_SET} options={liveOptions} disabled={!canChange || isSavingGraph} onChange={value_0 => onChange("liveConfigId", value_0)} />;
    $[13] = canChange;
    $[14] = data.liveConfigId;
    $[15] = id;
    $[16] = isSavingGraph;
    $[17] = liveOptions;
    $[18] = onChange;
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2">{t8}{t9}</div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== name || $[25] !== t10) {
    t11 = <div className="flex min-w-0 flex-1 flex-col gap-3">{name}{t10}</div>;
    $[24] = name;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== isReadOnly || $[28] !== isSavingGraph || $[29] !== onRemove || $[30] !== t) {
    t12 = !isReadOnly && <IconButton aria-label={t(_temp3)} size="compact" icon={IcoTrash16} disabled={isSavingGraph} onClick={onRemove} />;
    $[27] = isReadOnly;
    $[28] = isSavingGraph;
    $[29] = onRemove;
    $[30] = t;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t11 || $[33] !== t12) {
    t13 = <div className="flex items-end gap-3">{t2}{t11}{t12}</div>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== children || $[36] !== t13) {
    t14 = <Card.Body as="li" className="flex flex-col gap-2 px-4 py-3">{t13}{children}</Card.Body>;
    $[35] = children;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
};
const VaultProxyView = () => {
  const $ = _c(75);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "agentBuilder.tools.vaultProxy"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  const {
    state,
    actions,
    isReadOnly,
    isSavingGraph
  } = useBuilder();
  const {
    hasPermission
  } = usePermissions();
  const {
    data: session
  } = useSession();
  const hasLive = !session.org?.isSandboxRestricted;
  let t1;
  if ($[1] !== hasPermission) {
    t1 = hasPermission("manage_vault_proxy");
    $[1] = hasPermission;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const canSet = t1;
  const [isAdding, setIsAdding] = useState(false);
  const [draftName, setDraftName] = useState("");
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      isLive: true
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== hasLive) {
    t3 = {
      enabled: hasLive
    };
    $[4] = hasLive;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const liveQuery = useAllProxyConfigs(t2, t3);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      isLive: false
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const sandboxQuery = useAllProxyConfigs(t4);
  let T0;
  let canEdit;
  let showDraft;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[7] !== actions || $[8] !== canSet || $[9] !== draftName || $[10] !== hasLive || $[11] !== isAdding || $[12] !== isReadOnly || $[13] !== liveQuery || $[14] !== sandboxQuery.data || $[15] !== sandboxQuery.error || $[16] !== sandboxQuery.isPending || $[17] !== state.bundle.tools || $[18] !== t) {
    const proxyTools = state.bundle.tools.flatMap(_temp4);
    canEdit = !isReadOnly && canSet;
    showDraft = canEdit && (isAdding || proxyTools.length === 0);
    let t9;
    if ($[26] !== draftName) {
      t9 = draftName !== "" && !TOOL_NAME.test(draftName);
      $[26] = draftName;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    const isNameInvalid = t9;
    let t10;
    if ($[28] !== draftName) {
      t10 = t11 => {
        const {
          tool: tool_0
        } = t11;
        return tool_0.data.name === draftName;
      };
      $[28] = draftName;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    const isNameTaken = proxyTools.some(t10);
    let t11;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = () => {
        setIsAdding(false);
        setDraftName("");
      };
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    const cancelDraft = t11;
    let t12;
    if ($[31] !== actions || $[32] !== draftName || $[33] !== state.bundle.tools) {
      t12 = (slot, value) => {
        if (value === NOT_SET) {
          return;
        }
        cancelDraft();
        const data = withSlot({
          name: draftName
        }, slot, value);
        actions.setTools([...state.bundle.tools, {
          kind: "vault_proxy",
          data
        }]);
      };
      $[31] = actions;
      $[32] = draftName;
      $[33] = state.bundle.tools;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    const addTool = t12;
    let t13;
    if ($[35] !== actions || $[36] !== state.bundle.tools) {
      t13 = (index_0, slot_0, value_0) => {
        const configId = value_0 === NOT_SET ? undefined : value_0;
        const next = state.bundle.tools.flatMap((tool_1, i) => {
          if (i !== index_0 || !isVaultProxyTool(tool_1)) {
            return [tool_1];
          }
          const data_0 = withSlot(tool_1.data, slot_0, configId);
          return data_0.liveConfigId || data_0.sandboxConfigId ? [{
            ...tool_1,
            data: data_0
          }] : [];
        });
        actions.setTools(next);
      };
      $[35] = actions;
      $[36] = state.bundle.tools;
      $[37] = t13;
    } else {
      t13 = $[37];
    }
    const setSlot = t13;
    let t14;
    if ($[38] !== actions || $[39] !== state.bundle.tools) {
      t14 = index_1 => actions.setTools(state.bundle.tools.filter((_, i_0) => i_0 !== index_1));
      $[38] = actions;
      $[39] = state.bundle.tools;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    const removeTool = t14;
    let t15;
    if ($[41] !== t) {
      t15 = t16 => {
        const configs = t16 === undefined ? [] : t16;
        return [{
          value: NOT_SET,
          label: t(_temp5)
        }, ...configs.map(t17 => {
          const {
            id,
            name,
            status
          } = t17;
          return {
            value: id,
            label: status === "disabled" ? t(_temp6, {
              name
            }) : name
          };
        })];
      };
      $[41] = t;
      $[42] = t15;
    } else {
      t15 = $[42];
    }
    const options = t15;
    const renderTools = () => {
      if (sandboxQuery.isPending || hasLive && liveQuery.isPending) {
        return <div role="status" aria-label={t(_temp7)} className="flex flex-col gap-1">{[0, 1].map(_temp8)}</div>;
      }
      const error = sandboxQuery.error ?? liveQuery.error;
      if (error) {
        return <Card.Body className="px-4 py-3"><ErrorComponent error={error} /></Card.Body>;
      }
      if (!sandboxQuery.data?.length && !liveQuery.data?.length) {
        return <Card.Empty>{t(_temp9)}</Card.Empty>;
      }
      if (proxyTools.length === 0 && !showDraft) {
        return <Card.Empty>{t(_temp10)}</Card.Empty>;
      }
      const sandboxOptions = options(sandboxQuery.data);
      const liveOptions = hasLive ? options(liveQuery.data) : undefined;
      const draftHint = isNameTaken ? t(_temp11) : isNameInvalid ? t(_temp12) : t(_temp13);
      return <Card.List aria-label={t(_temp14)}>{proxyTools.map(t16 => {
          const {
            index: index_3,
            tool: tool_2
          } = t16;
          return <VaultProxyRow key={index_3} id={String(index_3)} name={<p className="text-label-2 text-primary">{tool_2.data.name}</p>} data={tool_2.data} sandboxOptions={sandboxOptions} liveOptions={liveOptions} canChange={canEdit} onChange={(slot_1, value_1) => setSlot(index_3, slot_1, value_1)} onRemove={() => removeTool(index_3)}>{!tool_2.data.sandboxConfigId && <p className="text-body-3 text-warning">{t(_temp15)}</p>}{hasLive && !tool_2.data.liveConfigId && <p className="text-body-3 text-warning">{t(_temp16)}</p>}</VaultProxyRow>;
        })}{showDraft && <VaultProxyRow id="new" name={<TextInput id="vault-proxy-new-name" label={t(_temp17)} size="compact" value={draftName} maxLength={40} onChangeText={setDraftName} placeholder={t(_temp18)} hasError={isNameTaken || isNameInvalid} hint={draftHint} />} data={{
          name: draftName
        }} sandboxOptions={sandboxOptions} liveOptions={liveOptions} canChange={draftName !== "" && !isNameInvalid && !isNameTaken} onChange={addTool} onRemove={cancelDraft} />}</Card.List>;
    };
    t7 = "max-w-[800px] mx-auto px-6 py-8 sm:px-10 flex flex-col gap-8";
    let t17;
    if ($[43] !== t) {
      t17 = t(_temp19);
      $[43] = t;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    let t18;
    if ($[45] !== t17) {
      t18 = <h2 className="text-heading-2 text-primary">{t17}</h2>;
      $[45] = t17;
      $[46] = t18;
    } else {
      t18 = $[46];
    }
    let t19;
    if ($[47] !== t) {
      t19 = t(_temp20);
      $[47] = t;
      $[48] = t19;
    } else {
      t19 = $[48];
    }
    let t20;
    if ($[49] !== t19) {
      t20 = <p className="text-body-2 text-tertiary">{t19}</p>;
      $[49] = t19;
      $[50] = t20;
    } else {
      t20 = $[50];
    }
    if ($[51] !== t18 || $[52] !== t20) {
      t8 = <div className="flex flex-col gap-1">{t18}{t20}</div>;
      $[51] = t18;
      $[52] = t20;
      $[53] = t8;
    } else {
      t8 = $[53];
    }
    t6 = "@container flex flex-col items-center gap-2";
    T0 = Card.Root;
    t5 = renderTools();
    $[7] = actions;
    $[8] = canSet;
    $[9] = draftName;
    $[10] = hasLive;
    $[11] = isAdding;
    $[12] = isReadOnly;
    $[13] = liveQuery;
    $[14] = sandboxQuery.data;
    $[15] = sandboxQuery.error;
    $[16] = sandboxQuery.isPending;
    $[17] = state.bundle.tools;
    $[18] = t;
    $[19] = T0;
    $[20] = canEdit;
    $[21] = showDraft;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    T0 = $[19];
    canEdit = $[20];
    showDraft = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
  }
  let t9;
  if ($[54] !== T0 || $[55] !== t5) {
    t9 = <T0>{t5}</T0>;
    $[54] = T0;
    $[55] = t5;
    $[56] = t9;
  } else {
    t9 = $[56];
  }
  let t10;
  if ($[57] !== canEdit || $[58] !== isSavingGraph || $[59] !== showDraft || $[60] !== t) {
    t10 = canEdit && !showDraft && <Button variant="secondary" size="compact" prefixIcon={IcoPlusSmall16} disabled={isSavingGraph} onClick={() => setIsAdding(true)}>{t(_temp21)}</Button>;
    $[57] = canEdit;
    $[58] = isSavingGraph;
    $[59] = showDraft;
    $[60] = t;
    $[61] = t10;
  } else {
    t10 = $[61];
  }
  let t11;
  if ($[62] !== canSet || $[63] !== isReadOnly || $[64] !== t) {
    t11 = !isReadOnly && !canSet && <p className="text-body-3 text-tertiary">{t(_temp22)}</p>;
    $[62] = canSet;
    $[63] = isReadOnly;
    $[64] = t;
    $[65] = t11;
  } else {
    t11 = $[65];
  }
  let t12;
  if ($[66] !== t10 || $[67] !== t11 || $[68] !== t6 || $[69] !== t9) {
    t12 = <section className={t6}>{t9}{t10}{t11}</section>;
    $[66] = t10;
    $[67] = t11;
    $[68] = t6;
    $[69] = t9;
    $[70] = t12;
  } else {
    t12 = $[70];
  }
  let t13;
  if ($[71] !== t12 || $[72] !== t7 || $[73] !== t8) {
    t13 = <div className={t7}>{t8}{t12}</div>;
    $[71] = t12;
    $[72] = t7;
    $[73] = t8;
    $[74] = t13;
  } else {
    t13 = $[74];
  }
  return t13;
};
export default VaultProxyView;
function _temp($) {
  return $.sandbox;
}
function _temp2($_0) {
  return $_0.live;
}
function _temp3($_1) {
  return $_1.remove;
}
function _temp4(tool, index) {
  return isVaultProxyTool(tool) ? [{
    index,
    tool
  }] : [];
}
function _temp5($) {
  return $.notSet;
}
function _temp6($_0) {
  return $_0.disabledOption;
}
function _temp7($_1) {
  return $_1.loading;
}
function _temp8(index_2) {
  return <Card.Body key={index_2} className="px-4 py-3"><Shimmer height="40px" width="100%" /></Card.Body>;
}
function _temp9($_2) {
  return $_2.noProxies;
}
function _temp10($_3) {
  return $_3.emptyState;
}
function _temp11($_4) {
  return $_4.duplicateName;
}
function _temp12($_5) {
  return $_5.invalidName;
}
function _temp13($_6) {
  return $_6.nameHint;
}
function _temp14($_7) {
  return $_7.title;
}
function _temp15($_8) {
  return $_8.missingSandbox;
}
function _temp16($_9) {
  return $_9.missingLive;
}
function _temp17($_10) {
  return $_10.name;
}
function _temp18($_11) {
  return $_11.namePlaceholder;
}
function _temp19($_12) {
  return $_12.title;
}
function _temp20($_13) {
  return $_13.subtitle;
}
function _temp21($_14) {
  return $_14.add;
}
function _temp22($_15) {
  return $_15.needsPermission;
}